import { KeyboardButtonProps } from '@ncr-design-system/keyboard';
import {
  Keyboard as NUIKeyboard,
  KeyboardLayout as NUIKeyboardLayout,
} from '@ncr-design-system/keyboard-react';
import React, { useCallback, useEffect, useState } from 'react';
import { CustomKeys } from '../common/keyboard-custom-keys/custom-keys';
import { styles } from './keyboard-styles';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const onKeyboardAction = event => {
  const action = event.detail;
  onKeyboardInputAction();

  if (action === 'enter') {
    // preform submit
    const form = document.activeElement?.closest('form') as HTMLFormElement | null;
    if (form) {
      const submit = new Event('submit', { cancelable: true, bubbles: true });
      form.dispatchEvent(submit);
    }
  } else if (action === 'next') {
    focusOnNextInputElement();
  } else if (action === 'clear') {
    const activeElement = document.activeElement as HTMLInputElement;
    triggerInputEvent(activeElement, '');
  }
};

const onKeyboardInputAction = () => {
  const activeElement = document.activeElement as HTMLInputElement;
  document.body.classList[
    activeElement?.attributes['data-customdisable-enter']?.value === 'true' ? 'add' : 'remove'
  ]('keyboard-disable-enter');
};

const triggerInputEvent = (activeElement, enteredValue) => {
  const lastValue = activeElement.value;
  activeElement.value = enteredValue;
  const event = new Event('input', { bubbles: true });
  const tracker = activeElement['_valueTracker'];
  if (tracker) {
    tracker.setValue(lastValue);
  }
  activeElement.dispatchEvent(event);
};

const focusOnNextInputElement = () => {
  let parent = document.activeElement?.closest('form') as HTMLFormElement | null;
  if (parent === null) {
    parent = document.activeElement?.closest('.MuiDialog-root') as HTMLFormElement | null;
  }
  const inputs = parent?.querySelectorAll('input') || [];

  for (let i = 0; i < inputs.length; i++) {
    if (inputs[i] === document.activeElement) {
      const next = inputs[i + 1];
      if (next) {
        next.focus();
        break;
      } else {
        inputs[0].focus();
      }
    }
  }
};

const Keyboard = (): JSX.Element | null => {
  styles();

  const [customKeys, setCustomKeys] = useState<{ [key: string]: KeyboardButtonProps }>();

  const onKeyboardOpened = useCallback((event): void => {
    if (event && event.target && event.target.localName === 'input') {
      let customButtonsAttrValue;
      if (event.target.attributes['data-custombuttons']) {
        customButtonsAttrValue = event.target.attributes['data-custombuttons'].value;
      }
      const customKeysObj =
        CustomKeys[customButtonsAttrValue] || CustomKeys['keyboardWithEnterButton'];
      setCustomKeys(customKeysObj);
      onKeyboardInputAction();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keyboardActionDispatch', onKeyboardAction);
    document.addEventListener('focusin', onKeyboardOpened);
    document.addEventListener('keyup', onKeyboardInputAction);
    document.addEventListener('focusout', onKeyboardInputAction);
    return () => {
      document.removeEventListener('keyboardActionDispatch', onKeyboardAction);
      document.removeEventListener('focusin', onKeyboardOpened);
      document.removeEventListener('keyup', onKeyboardInputAction);
      document.removeEventListener('focusout', onKeyboardInputAction);
    };
  }, [onKeyboardOpened]);

  if (window.Cypress) {
    return null;
  }

  return (
    <NUIKeyboard global={true} autoOpenClose translate='yes' maxWidth='97.51vw'>
      <NUIKeyboardLayout
        layout='condensedNumpad'
        language='english'
        minHeight='38.54vh'
        translate='yes'
        customKeys={customKeys}
      />
    </NUIKeyboard>
  );
};

export default Keyboard;
